"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, MapPin, Search } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function Hero() {
  const prefersReducedMotion = useReducedMotion();
  const trustHighlights = [{
    text: "Direct consultant access"
  }, {
    text: "Tailored UK & regional strategy"
  }, {
    text: "Clear, transparent reporting"
  }, {
    text: "Zero agency overheads"
  }];
  const featuredLocations = ["London", "Manchester", "Birmingham", "Leeds", "Bristol", "Swindon"];
  const initialProps = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const animateProps = {
    opacity: 1,
    y: 0
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-28 pb-20 md:pt-36 md:pb-28 border-b border-border">
      {/* Subtle decorative background glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/60 blur-3xl opacity-60" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-primary/5 blur-3xl opacity-50" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Main Hero Content */}
          <div className="lg:col-span-7 space-y-6">
            <motion.div initial={initialProps} animate={animateProps} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="inline-flex items-center gap-2">
              <Badge variant="outline" className="rounded-full px-3.5 py-1 text-xs font-medium border-border bg-card shadow-2xs text-foreground">
                <span className="flex size-2 rounded-full bg-accent animate-pulse mr-1" />
                Freelance SEO Consultant &bull; Swindon & Nationwide
              </Badge>
            </motion.div>

            <motion.h1 initial={initialProps} animate={animateProps} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Professional SEO consultant for sustainable search growth
            </motion.h1>

            <motion.p initial={initialProps} animate={animateProps} transition={{
            duration: 0.5,
            delay: 0.18,
            ease: "easeOut"
          }} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              You work directly with me. As an independent SEO strategy consultant, I deliver hands-on, high-impact SEO consultancy services and focused local SEO for UK businesses targeting regional, national, and international reach.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={initialProps} animate={animateProps} transition={{
            duration: 0.5,
            delay: 0.25,
            ease: "easeOut"
          }} className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button asChild size="lg" className="rounded-md font-medium text-base shadow-sm hover:opacity-95">
                <Link to="/ContactPage">
                  <span>Book a free SEO consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium text-base border-border bg-card hover:bg-muted text-foreground">
                <Link to="/CaseStudiesPage">
                  <span>Explore my case studies</span>
                </Link>
              </Button>
            </motion.div>

            {/* Direct Consultant Value Proof Strip */}
            <motion.div initial={initialProps} animate={animateProps} transition={{
            duration: 0.5,
            delay: 0.32,
            ease: "easeOut"
          }} className="grid grid-cols-2 gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-4 border-t border-border/80">
              {trustHighlights.map((highlight, index) => <div key={index} data-index={index} className="flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{highlight.text}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Visual / Personal Consultant Card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div initial={initialProps} animate={animateProps} transition={{
            duration: 0.6,
            delay: 0.2,
            ease: "easeOut"
          }} className="w-full max-w-md">
              <div className="relative rounded-2xl bg-card border border-border p-6 shadow-lg">
                {/* Profile Photo Area */}
                <div className="relative mb-5 overflow-hidden rounded-xl bg-muted aspect-[4/4.2]">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant at YE Consultancy" loading="lazy" decoding="async" className="h-full w-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-40" />
                  <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-background/95 p-3 backdrop-blur-xs border border-border/60">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Yassine Essaadi</p>
                        <p className="text-xs text-muted-foreground">Founder &amp; Principal SEO Consultant</p>
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium text-foreground">
                        YE Consultancy
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Interactive highlight stats box */}
                <div className="space-y-3 rounded-lg bg-background p-4 border border-border/70 text-xs">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium text-foreground">
                      <Search className="size-3.5 text-primary" />
                      Core Search Specialisms
                    </span>
                    <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                      Bespoke
                    </span>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    Technical audits, national visibility, commercial B2B SEO, and AI search optimisation built to generate sustainable revenue.
                  </p>

                  <div className="pt-2 border-t border-border/60">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-2">
                      <MapPin className="size-3 text-accent shrink-0" />
                      <span className="text-[11px] font-medium text-foreground">
                        Regional SEO support available for UK markets:
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {featuredLocations.map((city, idx) => <span key={idx} data-index={idx} className="rounded-sm bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {city}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}